import { useEffect, useState } from 'react';
import { useTranslation } from '../i18n';
import './ThemeToggle.css';

type Theme = 'light' | 'dark';

function readTheme(): Theme {
  try {
    const saved = localStorage.getItem('cf-theme');
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    /* ignore */
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  try {
    localStorage.setItem('cf-theme', theme);
  } catch {
    /* ignore */
  }
}

export function ThemeToggle() {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>(readTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const next: Theme = theme === 'light' ? 'dark' : 'light';
  const toggle = () => setTheme(next);

  const label =
    next === 'dark'
      ? t('a11y.toggle_theme_to_dark')
      : t('a11y.toggle_theme_to_light');

  return (
    <button
      type="button"
      className="cf-theme-toggle"
      onClick={toggle}
      aria-pressed={theme === 'dark'}
      aria-label={label}
    >
      {/* Symbol + visible text; never color alone (colorblind constraint). */}
      <span aria-hidden="true" className="cf-theme-toggle__icon">
        {theme === 'light' ? '☾' : '☀'}
      </span>
      <span className="cf-theme-toggle__label">
        {theme === 'light' ? 'Dark' : 'Light'}
      </span>
    </button>
  );
}
